import React, { useRef } from 'react';
import { StationSector } from '../../../../../../utils/types';
import { FormInput, FormSection, AircraftTypeAutocomplete } from '../../../../common';
import AirplanemodeActiveIcon from '@mui/icons-material/AirplanemodeActive';

interface AircraftDetailsSectionProps {
  station: StationSector;
  sectorIndex: number;
  flightOwner: string;
  onUpdate: (key: keyof StationSector, value: StationSector[keyof StationSector]) => void;
  getFieldError?: (sectorIndex: number, fieldName: keyof StationSector) => string | null;
  handleBlur?: (sectorIndex: number, fieldName: keyof StationSector, value: StationSector[keyof StationSector], station?: StationSector) => void;
  revalidateIfTouched?: (sectorIndex: number, fieldName: keyof StationSector, value: StationSector[keyof StationSector], station?: StationSector) => void;
}

export function AircraftDetailsSection({
  station,
  sectorIndex,
  flightOwner,
  onUpdate,
  getFieldError,
  handleBlur,
  revalidateIfTouched,
}: AircraftDetailsSectionProps) {
  const flightNumRef = useRef<HTMLInputElement>(null);

  // Parse stored value "AK 1234" into code and number parts
  const onwardParts = (station.onwardFlight || '').split(' ');
  const onwardCode = onwardParts[0] || '';
  const onwardNum = onwardParts.slice(1).join('') || '';

  const updateOnwardFlight = (code: string, num: string) => {
    const combined = num ? `${code} ${num}` : code;
    onUpdate('onwardFlight', combined || null);
    revalidateIfTouched?.(sectorIndex, 'onwardFlight', combined || null);
  };

  // Prefill aircraft owner from flight owner if not yet set
  React.useEffect(() => {
    if (flightOwner && !station.aircraftOwner) {
      onUpdate('aircraftOwner', flightOwner);
    }
  }, [flightOwner]);

  return (
    <FormSection
      title="Aircraft Details"
      icon={<AirplanemodeActiveIcon sx={{ fontSize: 20 }} />}
      collapsible={false}
    >
      <div className="grid grid-cols-[0.8fr_1.2fr_0.9fr_0.9fr_1.2fr] gap-x-4">
        <FormInput
          label="AC Owner"
          value={station.aircraftOwner || ''}
          onChange={v => {
            onUpdate('aircraftOwner', v);
            revalidateIfTouched?.(sectorIndex, 'aircraftOwner', v);
          }}
          onBlur={() => handleBlur?.(sectorIndex, 'aircraftOwner', station.aircraftOwner)}
          required
          error={getFieldError?.(sectorIndex, 'aircraftOwner')}
        />
        <AircraftTypeAutocomplete
          label="Aircraft Type"
          value={station.iataSubTypeCode}
          onChange={v => {
            onUpdate('iataSubTypeCode', v);
            revalidateIfTouched?.(sectorIndex, 'iataSubTypeCode', v);
          }}
          onSelect={acType => {
            onUpdate('iataSubTypeCode', acType.aircraftType);
            revalidateIfTouched?.(sectorIndex, 'iataSubTypeCode', acType.aircraftType);
          }}
          onBlur={() => handleBlur?.(sectorIndex, 'iataSubTypeCode', station.iataSubTypeCode)}
          error={getFieldError?.(sectorIndex, 'iataSubTypeCode')}
        />
        <FormInput
          label="Tail Number"
          value={station.tailNo || ''}
          onChange={v => {
            onUpdate('tailNo', v);
            revalidateIfTouched?.(sectorIndex, 'tailNo', v);
          }}
          onBlur={() => handleBlur?.(sectorIndex, 'tailNo', station.tailNo)}
          error={getFieldError?.(sectorIndex, 'tailNo')}
        />
        <FormInput
          label="Configuration"
          value={station.aircraftConfiguration}
          onChange={v => {
            onUpdate('aircraftConfiguration', v);
            revalidateIfTouched?.(sectorIndex, 'aircraftConfiguration', v);
          }}
          onBlur={() => handleBlur?.(sectorIndex, 'aircraftConfiguration', station.aircraftConfiguration)}
          error={getFieldError?.(sectorIndex, 'aircraftConfiguration')}
        />
        {/* Onward Flight - split into airline code + flight number */}
        <label className="block mb-2">
          <span className="block mb-1.5 text-base font-semibold text-gray-600">
            Onward Flight
          </span>
          <div className="flex">
            <input
              type="text"
              value={onwardCode}
              onChange={e => {
                const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 3);
                updateOnwardFlight(v, onwardNum);
                if (v.length >= 2 && e.target.value.length > onwardCode.length) {
                  flightNumRef.current?.focus();
                }
              }}
              onBlur={() => handleBlur?.(sectorIndex, 'onwardFlight', station.onwardFlight)}
              placeholder=""
              maxLength={3}
              className="w-16 px-3 py-2.5 border border-gray-300 rounded-l-lg text-gray-900 text-lg font-mono text-center outline-none transition-[border-color,box-shadow] bg-white focus:border-primary-500 focus:ring-3 focus:ring-primary-500/10 border-r-0"
            />
            <input
              ref={flightNumRef}
              type="text"
              inputMode="numeric"
              value={onwardNum}
              onChange={e => {
                const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
                updateOnwardFlight(onwardCode, v);
              }}
              onBlur={() => handleBlur?.(sectorIndex, 'onwardFlight', station.onwardFlight)}
              placeholder=""
              maxLength={4}
              className="flex-1 px-3 py-2.5 border border-gray-300 rounded-r-lg text-gray-900 text-lg font-mono outline-none transition-[border-color,box-shadow] bg-white focus:border-primary-500 focus:ring-3 focus:ring-primary-500/10"
            />
          </div>
        </label>
      </div>
    </FormSection>
  );
}
